import Logger from "../lib/logger";

/**
 * MegaMenuLoader
 *
 * Lazy-loads mega-menu content via Shopify's Section Rendering API so only
 * level 1 (the top nav bar / mobile level-1 items) is rendered on page load.
 *
 * Endpoints:
 * - Panels (levels 2-3): /?section_id=api-menu-panel
 *   One cached response contains keyed desktop/mobile fragments. The loader
 *   injects only the handles needed by the panel being opened.
 * - Products (level 4): <collection.url>?section_id=api-menu-products[-mobile]
 *   Rendered in native collection context.
 *
 * Strategy (network eager, DOM lazy):
 * - Panels (levels 2-3): the HTML for ALL panels is fetched in parallel into
 *   an in-memory cache as soon as level 1 is interactive (current viewport's
 *   mode first, the other right after) - but it is NOT injected into the DOM.
 *   Injection happens on hover/focus/tap. Because the cache resolves in a
 *   microtask, injection completes before the next paint, so users see no
 *   loading state; the DOM only grows by the panels actually opened.
 * - Products (level 4): warmed into the cache as soon as their level-3
 *   container is reached (category hover on desktop, submenu shown on
 *   mobile), injected per collection when hovered/tapped.
 * - All responses are cached in-memory per URL; in-flight requests are deduped.
 */

const PANEL_SECTION = "api-menu-panel";
const PRODUCTS_SECTIONS = {
	d: "api-menu-products",
	m: "api-menu-products-mobile",
};
const HOVER_ACTIVE_CLASS = "menu-hover-active";
const HOVER_PANEL_CLASS = "menu-has-hover-panel";
const HOVER_PANEL_SELECTOR =
	`:scope > .collection-lists, :scope > .product-list, :scope > .product, :scope > .submenu__resources`;
const HOVER_DIAGONAL_DELAY = 220;
const HOVER_CLOSE_DELAY = 160;

export default class MegaMenuLoader {
	constructor(dom) {
		// Vars
		// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
		this.NAME = `MegaMenuLoader`;

		this.dom = dom;
		this.panelBaseUrl = dom.getAttribute(`data-menu-panel-url`) || `/collections/all`;
		this.cache = new Map(); // url -> Promise<string>
		this.desktopMedia = window.matchMedia(`(min-width: 1024px)`);
	}

	// Logic
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
	init() {
		Logger.info(`${this.NAME} > init`);
		this.initDesktopHoverFetch();
		this.initMobilePrefetch();
		this.prefetchAllPanels();
	}

	/**
	 * fetchSection - Fetch a section-rendering URL with caching + in-flight dedupe.
	 * Returns a promise resolving to the section's inner HTML.
	 */
	fetchSection(url) {
		if (!this.cache.has(url)) {
			const promise = fetch(url, { headers: { Accept: `text/html` } })
				.then((response) => {
					if (!response.ok) {
						throw new Error(`${this.NAME} > fetchSection ${response.status} for ${url}`);
					}
					return response.text();
				})
				.then((html) => this.extractSectionInner(html))
				.catch((error) => {
					// Allow retries after a failure
					this.cache.delete(url);
					throw error;
				});
			this.cache.set(url, promise);
		}
		return this.cache.get(url);
	}

	/**
	 * extractSectionInner - Strip the `<div id="shopify-section-..." class="shopify-section">`
	 * wrapper the Section Rendering API adds, so injected markup matches the
	 * original server-rendered structure (important for the CSS hover selectors).
	 */
	extractSectionInner(html) {
		const template = document.createElement(`template`);
		template.innerHTML = html;
		const wrapper = template.content.querySelector(`.shopify-section`);
		return wrapper ? wrapper.innerHTML : html;
	}

	/**
	 * panelUrl - Build the Section Rendering API URL for a lazy panel container.
	 */
	panelUrl(container) {
		const joiner = this.panelBaseUrl.indexOf(`?`) > -1 ? `&` : `?`;
		return `${this.panelBaseUrl}${joiner}section_id=${PANEL_SECTION}`;
	}

	/**
	 * selectPanelHtml - Select only this container's keyed fragments from the
	 * shared panel response. Mobile category-specific CTAs are filtered here.
	 */
	selectPanelHtml(html, container) {
		const mode = container.getAttribute(`data-menu-mode`) || `d`;
		const slug = container.getAttribute(`data-menu-slug`) || `-`;
		const handles = (container.getAttribute(`data-menu-handles`) || ``)
			.split(`,`)
			.filter(Boolean);
		const template = document.createElement(`template`);
		template.innerHTML = html;
		const fragments = new Map();
		const tails = new Map();

		template.content
			.querySelectorAll(`[data-menu-panel-item]`)
			.forEach((fragment) => {
				if (fragment.getAttribute(`data-menu-mode`) !== mode) return;
				fragments.set(fragment.getAttribute(`data-menu-handle`), fragment);
			});
		template.content
			.querySelectorAll(`[data-menu-panel-tail]`)
			.forEach((fragment) => {
				if (fragment.getAttribute(`data-menu-mode`) !== mode) return;
				tails.set(fragment.getAttribute(`data-menu-handle`), fragment);
			});

		const selected = document.createElement(`div`);
		handles.forEach((handle) => {
			const fragment = fragments.get(handle);
			if (!fragment) return;
			while (fragment.firstChild) selected.appendChild(fragment.firstChild);
		});
		handles.forEach((handle) => {
			const fragment = tails.get(handle);
			if (!fragment) return;
			while (fragment.firstChild) selected.appendChild(fragment.firstChild);
		});

		if (mode === `m`) {
			selected.querySelectorAll(`[data-menu-category]`).forEach((cta) => {
				if (cta.getAttribute(`data-menu-category`) !== slug) cta.remove();
			});
		}

		return selected.innerHTML;
	}

	/**
	 * loadPanel - Fetch and inject levels 2-3 for a `[data-menu-lazy-panel]` container.
	 */
	loadPanel(container) {
		if (!container || container.hasAttribute(`data-menu-loaded`)) {
			return Promise.resolve();
		}

		return this.fetchSection(this.panelUrl(container))
			.then((html) => {
				if (container.hasAttribute(`data-menu-loaded`)) return;
				container.innerHTML = this.selectPanelHtml(html, container);
				container.setAttribute(`data-menu-loaded`, ``);
				this.afterInject(container);
			})
			.catch((error) => {
				Logger.info(`${this.NAME} > loadPanel failed: ${error.message}`);
			});
	}

	/**
	 * loadProducts - Fetch and inject the level-4 product list for a
	 * `[data-menu-lazy-products]` stub (desktop or mobile flavor).
	 */
	loadProducts(stub) {
		if (!stub || stub.hasAttribute(`data-menu-loaded`)) {
			return Promise.resolve();
		}

		const url = this.productsUrl(stub);
		if (!url) return Promise.resolve();

		return this.fetchSection(url)
			.then((html) => {
				if (stub.hasAttribute(`data-menu-loaded`)) return;
				if (html.replace(/\s/g, ``) === ``) {
					// e.g. unpublished collection rendering an empty section
					this.renderProductsFallback(stub);
					return;
				}
				stub.innerHTML = html;
				stub.setAttribute(`data-menu-loaded`, ``);
				this.initDesktopHoverIntent(stub);
			})
			.catch((error) => {
				// e.g. a renamed collection whose old URL 301-redirects: never
				// leave the spinner forever - link to the collection instead
				// (a real navigation follows the redirect just fine).
				Logger.info(`${this.NAME} > loadProducts failed: ${error.message}`);
				this.renderProductsFallback(stub);
			});
	}

	/**
	 * renderProductsFallback - Replace a product stub's spinner with a plain
	 * link to the collection when its section can't be fetched (stale menu
	 * entries, redirected/unpublished collections, network errors).
	 */
	renderProductsFallback(stub) {
		if (stub.hasAttribute(`data-menu-loaded`)) return;

		const collectionUrl = stub.getAttribute(`data-collection-url`);
		const mode = stub.getAttribute(`data-menu-products-mode`) || `d`;

		stub.innerHTML = ``;
		stub.setAttribute(`data-menu-loaded`, ``);

		if (!collectionUrl) return;

		const link = document.createElement(`a`);
		link.setAttribute(`href`, collectionUrl);
		link.textContent = `View All`;

		if (mode === `m`) {
			const item = document.createElement(`div`);
			item.className = `submenu-item`;
			item.setAttribute(`data-submenu-4-item`, ``);
			link.className = `submenu-item__title`;
			item.appendChild(link);
			stub.appendChild(item);
		} else {
			const title = document.createElement(`p`);
			title.className = `product-list__title`;
			title.textContent = `Models`;
			const list = document.createElement(`ul`);
			list.className = `product-list__products`;
			const li = document.createElement(`li`);
			link.className = `submenu__link`;
			li.appendChild(link);
			list.appendChild(li);
			stub.appendChild(title);
			stub.appendChild(list);
		}
	}

	/**
	 * afterInject - Re-apply behaviors that used to be bound on DOMContentLoaded
	 * (scripts inside injected HTML never execute).
	 */
	afterInject(container) {
		this.initDesktopHoverIntent(container);

		// Resources menu: keep the first category highlighted until hovered
		// (moved here from an inline <script> in snippets/theme-submenu.liquid)
		container.querySelectorAll(`.js-resources-submenus`).forEach((item) => {
			if (item.hasAttribute(`data-menu-resources-bound`)) return;
			item.setAttribute(`data-menu-resources-bound`, ``);

			const activeItems = item.querySelectorAll(`.initially-active`);
			item.addEventListener(`pointerenter`, () => {
				activeItems.forEach((activeItem) => {
					activeItem.classList.remove(`initially-active`);
				});
			});
			item.addEventListener(`pointerleave`, () => {
				activeItems.forEach((activeItem) => {
					activeItem.classList.add(`initially-active`);
				});
			});
		});
	}

	/**
	 * initDesktopHoverIntent - Keep the current nested column active while the
	 * pointer travels diagonally toward it. A short activation delay applies
	 * only when switching between siblings; first-open and keyboard focus stay
	 * immediate.
	 */
	initDesktopHoverIntent(container) {
		const groupSelector =
			`.submenu-content, .submenu__categories, .collection-list__collections, .product-list__products, .column-menu > ul`;
		const groups = Array.prototype.slice.call(
			container.querySelectorAll(groupSelector)
		);

		if (container.matches(groupSelector)) groups.unshift(container);

		groups.forEach((group) => this.bindHoverIntentGroup(group));
	}

	/**
	 * bindHoverIntentGroup - Manage one row/column of sibling menu items.
	 */
	bindHoverIntentGroup(group) {
		if (group.hasAttribute(`data-menu-hover-intent-bound`)) return;

		const items = Array.prototype.filter.call(group.children, (item) => {
			if (!(item instanceof HTMLElement)) return false;
			if (group.classList.contains(`submenu-content`)) {
				return item.classList.contains(`submenu`);
			}
			return item.matches(`li:not(.submenu__list-title)`);
		});

		if (!items.length) return;

		group.setAttribute(`data-menu-hover-intent-bound`, ``);
		items.forEach((item) => {
			item.classList.toggle(
				HOVER_PANEL_CLASS,
				Boolean(item.querySelector(HOVER_PANEL_SELECTOR))
			);
		});

		const initialItem = items.find((item) =>
			item.classList.contains(`initially-active`)
		);
		let activeItem = initialItem;
		let activationTimer = null;
		let closeTimer = null;
		let pointerHistory = [];

		const cancelActivation = () => {
			if (activationTimer === null) return;
			window.clearTimeout(activationTimer);
			activationTimer = null;
		};

		const cancelClose = () => {
			if (closeTimer === null) return;
			window.clearTimeout(closeTimer);
			closeTimer = null;
		};

		const setActiveItem = (item) => {
			items.forEach((candidate) => {
				candidate.classList.remove(`initially-active`);
				candidate.classList.toggle(HOVER_ACTIVE_CLASS, candidate === item);
			});
			this.fitProductPanel(item);
			activeItem = item;
			activationTimer = null;
		};

		const requestActivation = (item, event = null, immediate = false) => {
			cancelActivation();
			cancelClose();

			if (activeItem === item && item.classList.contains(HOVER_ACTIVE_CLASS)) {
				return;
			}

			const shouldDelay =
				!immediate &&
				activeItem &&
				activeItem !== item &&
				activeItem.classList.contains(HOVER_ACTIVE_CLASS) &&
				this.isPointerHeadingToSubmenu(activeItem, event, pointerHistory);

			if (!shouldDelay) {
				setActiveItem(item);
				return;
			}

			activationTimer = window.setTimeout(
				() => setActiveItem(item),
				HOVER_DIAGONAL_DELAY
			);
		};

		items.forEach((item) => {
			item.addEventListener(`pointerenter`, (event) =>
				requestActivation(item, event)
			);
			item.addEventListener(`focusin`, () =>
				requestActivation(item, null, true)
			);
		});

		group.addEventListener(`pointerenter`, cancelClose);
		group.addEventListener(`pointermove`, (event) => {
			pointerHistory.push({
				x: event.clientX,
				y: event.clientY,
				time: event.timeStamp,
			});
			pointerHistory = pointerHistory
				.filter((point) => event.timeStamp - point.time <= 140)
				.slice(-12);
		});
		group.addEventListener(`pointerleave`, () => {
			cancelActivation();
			cancelClose();
			closeTimer = window.setTimeout(() => {
				items.forEach((item) => item.classList.remove(HOVER_ACTIVE_CLASS));
				if (initialItem) initialItem.classList.add(`initially-active`);
				activeItem = initialItem;
				pointerHistory = [];
				closeTimer = null;
			}, HOVER_CLOSE_DELAY);
		});

		group.addEventListener(`focusout`, () => {
			window.setTimeout(() => {
				if (group.contains(document.activeElement)) return;
				items.forEach((item) => item.classList.remove(HOVER_ACTIVE_CLASS));
				if (initialItem) initialItem.classList.add(`initially-active`);
				activeItem = initialItem;
			}, 0);
		});
	}

	/**
	 * isPointerHeadingToSubmenu - Delay a sibling switch only when the pointer
	 * is moving right toward the currently open child panel. Vertical movement
	 * between items remains immediate.
	 */
	isPointerHeadingToSubmenu(activeItem, event, pointerHistory) {
		if (!event || pointerHistory.length < 2) return false;

		const panel = activeItem.querySelector(HOVER_PANEL_SELECTOR);
		if (!(panel instanceof HTMLElement)) return false;

		const origin = pointerHistory[0];
		const current = { x: event.clientX, y: event.clientY };
		const horizontalTravel = current.x - origin.x;
		const panelRect = panel.getBoundingClientRect();

		if (horizontalTravel < 6 || panelRect.left <= current.x) return false;

		const projectedY =
			origin.y +
			((current.y - origin.y) * (panelRect.left - origin.x)) /
				horizontalTravel;

		return projectedY >= panelRect.top - 24 && projectedY <= panelRect.bottom + 24;
	}

	/**
	 * fitProductPanel - Keep the final product detail column inside the menu
	 * viewport instead of forcing a horizontal scrollbar at narrow desktop
	 * widths.
	 */
	fitProductPanel(item) {
		const panel = item.querySelector(`:scope > .product`);
		if (!(panel instanceof HTMLElement)) return;

		const panelLeft = panel.getBoundingClientRect().left;
		const viewportWidth = document.documentElement.clientWidth;
		const edgePadding = window.innerWidth >= 1340 ? 64 : 20;
		const availableWidth = viewportWidth - panelLeft - edgePadding;

		panel.style.width = `${Math.min(730, Math.max(280, availableWidth))}px`;
	}

	/**
	 * initDesktopHoverFetch - Fetch panels on hover/focus of a top-level item
	 * (covers the case where the user hovers before the idle prefetch has run)
	 * and product lists on hover of a collection item.
	 */
	initDesktopHoverFetch() {
		Logger.info(`${this.NAME} > initDesktopHoverFetch`);

		const self = this;

		const onOver = (e) => {
			if (!(e.target instanceof Element)) return;

			// Top-level item -> load its panel
			const listItem = e.target.closest(`.theme-header-nav__list-item--has-submenu`);
			if (listItem) {
				const panel = listItem.querySelector(`[data-menu-lazy-panel]`);
				if (panel) self.loadPanel(panel);
			}

			// Collection item -> inject its product list (direct child stub only)
			const li = e.target.closest(`li`);
			if (li && self.dom.contains(li)) {
				const stub = li.querySelector(`:scope > [data-menu-lazy-products]`);
				if (stub) self.loadProducts(stub);

				// Level 3 reached: hovering a category reveals its collection
				// lists -> warm the level-4 caches for those collections so
				// the product panel injects instantly when one is hovered.
				const collectionLists = li.querySelector(`:scope > .collection-lists`);
				if (collectionLists) self.warmNextLevelProducts(collectionLists);
			}

			// Pointer already inside an empty stub (e.g. via keyboard/fast moves)
			const insideStub = e.target.closest(`[data-menu-lazy-products]`);
			if (insideStub) self.loadProducts(insideStub);
		};

		this.dom.addEventListener(`pointerover`, onOver);
		this.dom.addEventListener(`focusin`, onOver);
	}

	/**
	 * initMobilePrefetch - Warm all mobile panels once the hamburger is opened,
	 * so tapping into level 2 feels instant.
	 */
	initMobilePrefetch() {
		Logger.info(`${this.NAME} > initMobilePrefetch`);

		const self = this;
		const hamburgerDom = this.dom.querySelector(`[data-theme-header-hamburger]`);

		hamburgerDom?.addEventListener(`click`, () => {
			self.prefetchPanels(`m`);
		});
	}

	/**
	 * productsUrl - Build the Section Rendering API URL for a product-list stub.
	 * Returns false when the stub has no collection URL.
	 */
	productsUrl(stub) {
		const collectionUrl = stub.getAttribute(`data-collection-url`);
		if (!collectionUrl) return false;

		const mode = stub.getAttribute(`data-menu-products-mode`) || `d`;
		const sectionId = PRODUCTS_SECTIONS[mode] || PRODUCTS_SECTIONS.d;
		const joiner = collectionUrl.indexOf(`?`) > -1 ? `&` : `?`;
		return `${collectionUrl}${joiner}section_id=${sectionId}`;
	}

	/**
	 * warmProducts - Fetch a product list into the cache WITHOUT touching the DOM.
	 */
	warmProducts(stub) {
		if (!stub || stub.hasAttribute(`data-menu-loaded`)) {
			return Promise.resolve();
		}

		const url = this.productsUrl(stub);
		if (!url) return Promise.resolve();

		return this.fetchSection(url).catch((error) => {
			Logger.info(`${this.NAME} > warmProducts failed: ${error.message}`);
		});
	}

	/**
	 * warmNextLevelProducts - Warm the product caches (level 4) for all stubs
	 * that belong directly to the given level-3 container - i.e. one level
	 * ahead of where the user is, never the whole tree.
	 */
	warmNextLevelProducts(container) {
		const self = this;

		container
			.querySelectorAll(`[data-menu-lazy-products]`)
			.forEach((stub) => {
				const owner = stub.parentElement
					? stub.parentElement.closest(
							`.mobile-nav__submenu, .collection-lists, [data-menu-lazy-panel]`
						)
					: null;
				if (owner === container) self.warmProducts(stub);
			});
	}

	/**
	 * warmPanel - Fetch a panel's HTML into the cache WITHOUT touching the DOM.
	 * Keeps the resting DOM at level 1 only; loadPanel() injects on demand.
	 */
	warmPanel(container) {
		if (!container || container.hasAttribute(`data-menu-loaded`)) {
			return Promise.resolve();
		}

		return this.fetchSection(this.panelUrl(container)).catch((error) => {
			Logger.info(`${this.NAME} > warmPanel failed: ${error.message}`);
		});
	}

	/**
	 * prefetchAllPanels - Warm the cache for every panel (levels 2-3) in
	 * parallel right away, so injection on hover/tap is instant. The current
	 * viewport's mode warms first; the other mode follows immediately after.
	 */
	prefetchAllPanels() {
		Logger.info(`${this.NAME} > prefetchAllPanels`);

		const self = this;
		const firstMode = this.desktopMedia.matches ? `d` : `m`;
		const secondMode = firstMode === `d` ? `m` : `d`;

		this.prefetchPanels(firstMode).then(() => self.prefetchPanels(secondMode));
	}

	/**
	 * prefetchPanels - Warm the cache for all panels of a mode ("d"/"m") in parallel.
	 */
	prefetchPanels(mode) {
		Logger.info(`${this.NAME} > prefetchPanels (${mode})`);

		const panels = this.dom.querySelectorAll(
			`[data-menu-lazy-panel][data-menu-mode="${mode}"]`
		);

		return Promise.all(
			Array.prototype.map.call(panels, (panel) => this.warmPanel(panel))
		);
	}
}
